/* ============================================================
   Админка · Документы — регламенты и положения для страниц
   «Регламенты» и «О федерации». Файл (PDF) грузится на сервер,
   ссылка на витрине ведёт прямо на него.
   ============================================================ */
import { useRef, useState } from 'react';
import { useFedContent, uploadUrl } from '../content';
import { useAppStore } from '../../store';
import { Ic } from '../icons';
import { AdmCard, AdmIconBtn, AdmPrimaryBtn } from './ui';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} МБ` : `${Math.max(1, Math.round(bytes / 1024))} КБ`;
};

const blank = () => ({ name_ru: '', name_kz: '', file: '', size: '' });

function DocEditor({ draft, setDraft, onSave, onCancel, busy }) {
  const uploadDoc = useFedContent((s) => s.uploadDoc);
  const fire = useAppStore((s) => s.fire);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const { path, size } = await uploadDoc(file);
      setDraft((d) => ({
        ...d,
        file: path,
        size: fmtSize(size),
        // имя по умолчанию — из имени файла, чтобы не заполнять вручную
        name_ru: d.name_ru || file.name.replace(/\.[^.]+$/, ''),
      }));
    } catch (err) {
      fire && fire(err.message || 'Не удалось загрузить файл', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdmCard style={{ padding: 22 }}>
      <div className="flex items-center justify-between border-b border-line" style={{ paddingBottom: 14, marginBottom: 16 }}>
        <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 15, letterSpacing: '.02em' }}>
          {draft.id ? 'Редактирование' : 'Новый документ'}
        </div>
        <button onClick={onCancel} className="fed-iconbtn" title="Закрыть"><Ic name="x" size={16} /></button>
      </div>

      <div className="fed-grid-form">
        <label className="flex flex-col gap-[7px]">
          <span className="fed-label">Название (рус)</span>
          <input className="fed-input" value={draft.name_ru} placeholder="Регламент CS2 OPEN · СКО" onChange={(e) => setDraft((d) => ({ ...d, name_ru: e.target.value }))} />
        </label>
        <label className="flex flex-col gap-[7px]">
          <span className="fed-label">Атауы (қаз)</span>
          <input className="fed-input" value={draft.name_kz} placeholder="пусто — покажется русское" onChange={(e) => setDraft((d) => ({ ...d, name_kz: e.target.value }))} />
        </label>
      </div>

      <div className="flex flex-col gap-[7px]" style={{ marginTop: 16 }}>
        <span className="fed-label">Файл (PDF или скан)</span>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center justify-center rounded-[10px] flex-shrink-0" style={{ width: 44, height: 44, background: 'rgba(225,18,23,.09)', border: '1px solid var(--fed-line-accent)', color: 'var(--fed-accent-bright)' }}>
            <Ic name="doc" size={18} />
          </span>
          <input ref={inputRef} type="file" accept="application/pdf,image/png,image/jpeg" hidden onChange={pick} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="fed-btn-ghost h-9 px-3.5 rounded-[10px]" style={{ fontSize: 12.5, opacity: uploading ? 0.6 : 1 }}>
            {uploading ? 'Загружаем…' : draft.file ? 'Заменить файл' : 'Загрузить файл'}
          </button>
          {draft.file && (
            <a href={uploadUrl(draft.file)} target="_blank" rel="noopener noreferrer" className="font-mono text-accent-bright no-underline" style={{ fontSize: 12 }}>
              открыть {draft.size ? `· ${draft.size}` : ''}
            </a>
          )}
        </div>
      </div>

      <div className="flex gap-2.5 border-t border-line" style={{ paddingTop: 16, marginTop: 18 }}>
        <AdmPrimaryBtn icon="check" onClick={onSave} className={draft.name_ru.trim() && draft.file && !busy ? '' : 'opacity-50 pointer-events-none'}>
          {busy ? 'Сохраняем…' : draft.id ? 'Сохранить' : 'Добавить'}
        </AdmPrimaryBtn>
        <button onClick={onCancel} className="fed-btn-ghost h-10 px-5 rounded-[10px]" style={{ fontSize: 13 }}>Отмена</button>
      </div>
      {!draft.file && <p className="text-fg-faint" style={{ fontSize: 11.5, marginTop: 10 }}>Без файла документ не сохранить — на витрине ссылка вела бы в никуда.</p>}
    </AdmCard>
  );
}

export default function AdmDocs() {
  const docs = useFedContent((s) => s.docs) || [];
  const saveDoc = useFedContent((s) => s.saveDoc);
  const deleteDoc = useFedContent((s) => s.deleteDoc);
  const moveDoc = useFedContent((s) => s.moveDoc);
  const fire = useAppStore((s) => s.fire);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (busy || !draft.name_ru.trim() || !draft.file) return;
    setBusy(true);
    const ok = await saveDoc({ ...draft, name_kz: draft.name_kz || draft.name_ru });
    setBusy(false);
    if (ok) { fire && fire('Документ сохранён', 'ok'); setDraft(null); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Удалить документ «${d.name_ru}»?`)) return;
    const ok = await deleteDoc(d.id);
    if (ok) fire && fire('Документ удалён', 'ok');
  };

  return (
    <div className="fed-admin-view flex flex-col gap-4">
      {draft ? (
        <DocEditor draft={draft} setDraft={setDraft} onSave={save} onCancel={() => setDraft(null)} busy={busy} />
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-fg-muted" style={{ fontSize: 13 }}>
            Записей: <b className="text-fg">{docs.length}</b> · показываются на страницах «Регламенты» и «О федерации»
          </div>
          <AdmPrimaryBtn onClick={() => setDraft(blank())}>Добавить документ</AdmPrimaryBtn>
        </div>
      )}

      <AdmCard style={{ padding: 20 }}>
        <div className="flex flex-col">
          {docs.map((d, i) => (
            <div key={d.id ?? i} className="flex items-center gap-3.5 border-t border-line first:border-t-0" style={{ padding: '12px 0' }}>
              <span className="flex items-center justify-center rounded-xl flex-shrink-0" style={{ width: 44, height: 44, background: 'rgba(225,18,23,.09)', border: '1px solid var(--fed-line-accent)', color: 'var(--fed-accent-bright)' }}>
                <Ic name="doc" size={19} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-fg truncate" style={{ fontSize: 14.5 }}>{d.name_ru}</div>
                <div className="font-mono text-fg-faint truncate" style={{ fontSize: 11, marginTop: 2 }}>{d.size || 'файл'}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <a href={uploadUrl(d.file)} target="_blank" rel="noopener noreferrer" className="fed-iconbtn no-underline" title="Открыть файл">
                  <Ic name="external" size={15} />
                </a>
                <button className="fed-iconbtn" title="Выше" onClick={() => moveDoc(d.id, -1)} disabled={i === 0} style={i === 0 ? { opacity: 0.3, cursor: 'default' } : undefined}>
                  <Ic name="chevD" size={16} style={{ transform: 'rotate(180deg)' }} />
                </button>
                <button className="fed-iconbtn" title="Ниже" onClick={() => moveDoc(d.id, 1)} disabled={i === docs.length - 1} style={i === docs.length - 1 ? { opacity: 0.3, cursor: 'default' } : undefined}>
                  <Ic name="chevD" size={16} />
                </button>
                <AdmIconBtn name="edit" title="Редактировать" onClick={() => setDraft({ ...d })} />
                <AdmIconBtn name="trash" tint="danger" title="Удалить" onClick={() => remove(d)} />
              </div>
            </div>
          ))}
          {!docs.length && <div className="text-fg-faint text-center" style={{ padding: '24px 0', fontSize: 13 }}>Документов нет — страница «Регламенты» пустая</div>}
        </div>
      </AdmCard>
    </div>
  );
}
